import React, { useEffect, useRef, useState } from 'react';
import type { SessionSnapshot } from './api';

interface Candle {
  time: number;
  datetime: string;
  open: number;
  high: number;
  low: number;
  close: number;
  is_session: boolean;
}

interface SessionMarkers {
  sessionOpen: number | null;
  sessionHigh: number | null;
  sessionLow: number | null;
  orbHigh: number | null;
  orbLow: number | null;
  prevClose: number | null;
  yestOpen: number | null;
  yestClose: number | null;
}

interface ChartData {
  symbol: string;
  date: string;
  timeframe: string;
  candles: Candle[];
  sessionMarkers: SessionMarkers | null;
}

interface OHLCDisplay {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

// lightweight-charts v5.x — global on window
declare const LightweightCharts: any;

async function fetchCandles(instrument: string, date: string): Promise<ChartData | null> {
  try {
    const LIVE_API_BASE = (window as any).__LIVE_API_BASE || '';
    const res = await fetch(`${LIVE_API_BASE}/api/candles/${instrument}/${date}`);
    if (!res.ok) return null;
    const data = await res.json();
    // If no session candles (or no candles at all), try walking backwards up to 5 days
    const hasSessionData = data.candles && data.candles.some((c: Candle) => c.is_session);
    if (!hasSessionData) {
      let prevDate = new Date(date + 'T00:00:00');
      for (let i = 0; i < 5; i++) {
        prevDate.setDate(prevDate.getDate() - 1);
        const prev = prevDate.toISOString().slice(0, 10);
        const res2 = await fetch(`${LIVE_API_BASE}/api/candles/${instrument}/${prev}`);
        if (res2.ok) {
          const prevData = await res2.json();
          if (prevData.candles && prevData.candles.some((c: Candle) => c.is_session)) {
            prevData._fallbackDate = prev;
            return prevData;
          }
        }
      }
    }
    return data;
  } catch {
    return null;
  }
}

export default function TradingViewChart({
  instrument,
  snapshot,
}: {
  instrument: string;
  snapshot: SessionSnapshot | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayDate, setDisplayDate] = useState<string>('');
  const [ohlc, setOhlc] = useState<OHLCDisplay | null>(null);
  const [sepX, setSepX] = useState<number | null>(null);

  // FTMO server runs on UTC+3 (EEST). Add offset so chart x-axis shows FTMO server time.
  const TZ_OFFSET_S = 3 * 3600;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      // Use today if we have it, otherwise fall back to yesterday
      const todayStr = displayDate || new Date().toISOString().slice(0, 10);
      const data = await fetchCandles(instrument, todayStr);
      if (!cancelled) {
        setChartData(data);
        if (data?.date) setDisplayDate(data.date);
        else if (data?._fallbackDate) setDisplayDate(data._fallbackDate);
        setLoading(false);
      }
    }
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [instrument, displayDate]);

  useEffect(() => {
    if (!chartData || !containerRef.current) return;
    if (typeof LightweightCharts === 'undefined') {
      setError('Chart library not loaded');
      return;
    }

    if (chartRef.current) {
      try { chartRef.current.remove(); } catch {}
      chartRef.current = null;
    }

    const container = containerRef.current;
    container.innerHTML = '';

    const chart = LightweightCharts.createChart(container, {
      layout: {
        background: { color: '#1e293b' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: '#334155' },
        horzLines: { color: '#334155' },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: 'rgba(147, 163, 184, 0.4)', width: 1, style: 2 },
        horzLine: { color: 'rgba(147, 163, 184, 0.4)', width: 1, style: 2 },
      },
      timeScale: {
        borderColor: '#475569',
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: true,
        fixRightEdge: false,
        rightOffset: 5,
        timezone: 'UTC+3',
      },
      width: container.clientWidth,
      height: 480,
      rightPriceScale: {
        borderColor: '#334155',
      },
    });

    chartRef.current = chart;

    const markers = chartData.sessionMarkers;

    // Pre-session and session candles — both visible
    const allCandles = chartData.candles;
    const sessionCandles = allCandles.filter(c => c.is_session);
    const preCandles = allCandles.filter(c => !c.is_session);

    // Build a lookup map: raw UTC candle time → candle
    const offsetCandleMap = new Map<number, Candle>();
    allCandles.forEach(c => offsetCandleMap.set(c.time, c));

    // Create separate series for pre-session and session so they're visually distinct
    // Timestamps are raw UTC; chart displays them in UTC+3 via timezone option
    const preCandleData = preCandles.map(c => ({
      time: c.time as number,
      open: c.open as number,
      high: c.high as number,
      low: c.low as number,
      close: c.close as number,
    }));
    const sessionCandleData = sessionCandles.map(c => ({
      time: c.time as number,
      open: c.open as number,
      high: c.high as number,
      low: c.low as number,
      close: c.close as number,
    }));

    // Candlestick series for pre-session — muted color so it doesn't overpower
    const preSeries = chart.addSeries(LightweightCharts.CandlestickSeries, {
      upColor: 'rgba(139, 92, 246, 0.55)',
      downColor: 'rgba(139, 92, 246, 0.55)',
      borderUpColor: 'rgba(139, 92, 246, 0.7)',
      borderDownColor: 'rgba(139, 92, 246, 0.7)',
      wickUpColor: 'rgba(139, 92, 246, 0.55)',
      wickDownColor: 'rgba(139, 92, 246, 0.55)',
    });

    // Candlestick series for regular session
    const candleSeries = chart.addSeries(LightweightCharts.CandlestickSeries, {
      upColor: '#00ff94',
      downColor: '#ff4060',
      borderUpColor: '#00ff94',
      borderDownColor: '#ff4060',
      wickUpColor: '#00ff94',
      wickDownColor: '#ff4060',
    });

    // Draw pre-session candles first (underneath)
    if (preCandleData.length > 0) {
      preSeries.setData(preCandleData);
    }

    if (sessionCandleData.length > 0) {
      candleSeries.setData(sessionCandleData);

      // Opening marker — blue arrow pointing down at session open price level
      if (markers?.sessionOpen != null) {
        try {
          const firstCandle = sessionCandleData[0];
          LightweightCharts.createSeriesMarkers(candleSeries, [
            {
              time: firstCandle.time,
              color: '#3b82f6',
              shape: 'arrowDown',
              text: 'OPEN',
              position: 'aboveBar',
              price: markers.sessionOpen,
            },
          ]);
        } catch {}
      }

      // Closing marker — orange arrow pointing up at today's last session candle
      try {
        const lastCandle = sessionCandleData[sessionCandleData.length - 1];
        LightweightCharts.createSeriesMarkers(candleSeries, [
          {
            time: lastCandle.time,
            color: '#f97316',
            shape: 'arrowUp',
            text: 'CLOSE',
            position: 'belowBar',
            price: lastCandle.close,
          },
        ]);
      } catch {}
    }

    // Yesterday's session close label — last candle of yesterday's official session
    // (not the last pre-session tick before today's open)
    if (preCandles.length > 0 && markers?.yestClose != null) {
      try {
        // Find the candle at yestClose timestamp (last candle in yesterday session window)
        const yestCloseCandle = preCandles
          .filter(c => !c.is_session)
          .sort((a, b) => a.time - b.time)
          .reverse()
          .find(c => c.close === markers.yestClose);
        if (yestCloseCandle) {
          LightweightCharts.createSeriesMarkers(preSeries, [
            {
              time: yestCloseCandle.time,
              color: '#a855f7',
              shape: 'arrowUp',
              text: 'YEST CLOSE',
              position: 'belowBar',
              price: yestCloseCandle.close,
            },
          ]);
        }
      } catch {}
    }

    // Yesterday's session open label — first candle of yesterday's session
    if (preCandles.length > 0 && markers?.yestOpen != null) {
      try {
        const sorted = [...preCandles].filter(c => !c.is_session).sort((a, b) => a.time - b.time);
        // yestOpen is the first candle of yesterday's session, so find the first one with that open price
        const yestOpenCandle = sorted.find(c => c.open === markers.yestOpen);
        if (yestOpenCandle) {
          LightweightCharts.createSeriesMarkers(preSeries, [
            {
              time: yestOpenCandle.time,
              color: '#a855f7',
              shape: 'arrowDown',
              text: 'YEST OPEN',
              position: 'aboveBar',
              price: yestOpenCandle.open,
            },
          ]);
        }
      } catch {}
    }

    // Session open line
    if (markers?.sessionOpen != null) {
      try {
        candleSeries.createPriceLine({
          price: markers.sessionOpen,
          color: '#3b82f6',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: 'Session Open',
        });
      } catch {}
    }

    // Previous day close line
    if (markers?.prevClose != null) {
      try {
        candleSeries.createPriceLine({
          price: markers.prevClose,
          color: '#f59e0b',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: 'Prev Close',
        });
      } catch {}
    }

    // Prev Session High line
    if (snapshot?.prev_high != null && snapshot.prev_high > 0) {
      try {
        candleSeries.createPriceLine({
          price: snapshot.prev_high,
          color: '#00ff94',
          lineWidth: 1,
          lineStyle: 1, // dotted
          axisLabelVisible: true,
          title: 'Prev Sess H',
        });
      } catch {}
    }

    // Prev Session Low line
    if (snapshot?.prev_low != null && snapshot.prev_low > 0) {
      try {
        candleSeries.createPriceLine({
          price: snapshot.prev_low,
          color: '#ff4060',
          lineWidth: 1,
          lineStyle: 1, // dotted
          axisLabelVisible: true,
          title: 'Prev Sess L',
        });
      } catch {}
    }

    // ── OHLC + time overlay via crosshair subscription ──
    chart.subscribeCrosshairMove((param: any) => {
      if (!param.time || !allCandles.length) {
        // Show last candle info as default when crosshair has no time
        const last = allCandles[allCandles.length - 1];
        if (last) {
          // Display in FTMO server time (UTC+3)
          const d = new Date(last.time * 1000);
          const h = String(d.getUTCHours()).padStart(2, '0');
          const min = String(d.getUTCMinutes()).padStart(2, '0');
          setOhlc({
            time: `${d.toISOString().slice(0, 10)} ${h}:${min}`,
            open: last.open,
            high: last.high,
            low: last.low,
            close: last.close,
          });
        }
        return;
      }
      // param.time is already in FTMO offset time (UTC+3 added to Unix timestamp)
      const t = param.time as number;
      const d = new Date(t * 1000); // already offset — getUTC* gives FTMO server time
      const dateStr = d.toISOString().slice(0, 10);
      const h = String(d.getUTCHours()).padStart(2, '0');
      const min = String(d.getUTCMinutes()).padStart(2, '0');
      const timeStr = `${h}:${min}`;

      // Find the candle at this FTMO chart time using the offset map
      const candle = offsetCandleMap.get(t);
      if (candle) {
        setOhlc({ time: `${dateStr} ${timeStr}`, open: candle.open, high: candle.high, low: candle.low, close: candle.close });
      } else {
        // Fallback: find nearest by raw UTC time
        const nearest = allCandles.reduce((prev, curr) =>
          Math.abs(curr.time - t) < Math.abs(prev.time - t) ? curr : prev
        );
        if (nearest) {
          setOhlc({ time: `${dateStr} ${timeStr}`, open: nearest.open, high: nearest.high, low: nearest.low, close: nearest.close });
        }
      }
    });

    // Also subscribe to click to force an initial crosshair position
    chart.subscribeClick((param: any) => {
      if (param.time && allCandles.length) {
        const t = param.time as number;
        const d = new Date(t * 1000); // already FTMO offset
        const candle = offsetCandleMap.get(t) || allCandles.reduce((prev, curr) =>
          Math.abs(curr.time - t) < Math.abs(prev.time - t) ? curr : prev
        );
        if (candle) {
          const dateStr = d.toISOString().slice(0, 10);
          const h = String(d.getUTCHours()).padStart(2, '0');
          const min = String(d.getUTCMinutes()).padStart(2, '0');
          setOhlc({ time: `${dateStr} ${h}:${min}`, open: candle.open, high: candle.high, low: candle.low, close: candle.close });
        }
      }
    });

    // Set visible range starting from midnight UTC today (matches MT5 chart view)
    // Pre-session candles before midnight are loaded but hidden by default
    if (allCandles.length > 10) {
      const lastCandle = allCandles[allCandles.length - 1];
      // midnight FTMO time (UTC+3) = 21:00 UTC previous day
      const nowUtc = new Date();
      const midnightFtmoS = Math.floor(Date.UTC(nowUtc.getUTCFullYear(), nowUtc.getUTCMonth(), nowUtc.getUTCDate()) / 1000) - (1 * 3600); // 00:00 FTMO = 21:00 UTC prev day
      // Use midnight FTMO as start, but don't go earlier than first available candle
      const firstCandle = allCandles[0];
      // visibleFrom is raw UTC seconds — chart displays in UTC+3 via timezone option
      const visibleFrom = Math.max(firstCandle.time, midnightFtmoS);
      const visibleRange = lastCandle.time - visibleFrom;
      const rightMarginS = Math.ceil(visibleRange * 0.25); // 25% empty right margin
      chart.timeScale().setVisibleRange({
        from: visibleFrom,
        to: lastCandle.time + rightMarginS,
      });
    } else {
      chart.timeScale().fitContent();
    }
    // Force time axis to show labels
    chart.timeScale().applyOptions({ timeVisible: true, secondsVisible: false });

    // ── Period separator: HTML overlay line at session open ──
    // Calculated AFTER fitContent so the coordinate system is established.
    if (markers?.sessionOpen != null && sessionCandleData.length > 0) {
      try {
        const ts = chart.timeScale();
        const coord = ts.timeToCoordinate(sessionCandleData[0].time);
        if (coord !== null) {
          setSepX(coord);
        }
      } catch {}
    }

    const handleResize = () => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    const ro = new ResizeObserver(handleResize);
    ro.observe(container);

    return () => {
      ro.disconnect();
      if (chartRef.current) {
        try { chartRef.current.remove(); } catch {}
        chartRef.current = null;
      }
    };
  }, [chartData]);

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <span style={styles.title}>📊 Chart</span>
          <span style={styles.badge}>{instrument}</span>
        </div>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <span style={styles.title}>📊 Chart</span>
          <span style={styles.badge}>{instrument}</span>
        </div>
        <div style={{ ...styles.loading, color: '#ef4444' }}>{error}</div>
      </div>
    );
  }

  const m = chartData?.sessionMarkers;
  const sessionCandles = chartData?.candles?.filter(c => c.is_session) ?? [];

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <span style={styles.title}>📊 Chart</span>
        <span style={styles.badge}>{instrument}</span>
        <span style={{ ...styles.dateBadge, color: '#f1f5f9', fontWeight: 700, fontSize: 12 }}>{displayDate || new Date().toISOString().slice(0, 10)}</span>
        <span style={{ ...styles.dateBadge, color: '#94a3b8', fontSize: 11 }}>(FTMO Server, UTC+3)</span>
        <span style={styles.legendDot} title="Pre-session (ghost)">◐ Pre-Session</span>
        <span style={{ ...styles.dateBadge, fontSize: 11, marginLeft: 4, color: '#94a3b8' }}>◐ = yesterday's M5</span>
        <span style={styles.legendDot2} title="Today close">▲ CLOSE (today)</span>
      </div>
      <div ref={containerRef} style={styles.chartContainer}>
        {/* Period separator — vertical dashed line at session open */}
        {sepX !== null && (
          <div style={{ ...styles.periodSeparator, left: sepX }} />
        )}
        {/* OHLC overlay — upper left corner */}
        {ohlc && (
          <div style={styles.ohlcOverlay}>
            <div style={styles.ohlcTime}>{ohlc.time} FTMO</div>
            <div style={styles.ohlcRow}>
              <span style={styles.ohlcLabel}>O</span>
              <span style={styles.ohlcValue}>{ohlc.open.toFixed(2)}</span>
              <span style={styles.ohlcLabel}>H</span>
              <span style={{ ...styles.ohlcValue, color: '#00ff94' }}>{ohlc.high.toFixed(2)}</span>
            </div>
            <div style={styles.ohlcRow}>
              <span style={styles.ohlcLabel}>L</span>
              <span style={{ ...styles.ohlcValue, color: '#ff4060' }}>{ohlc.low.toFixed(2)}</span>
              <span style={styles.ohlcLabel}>C</span>
              <span style={{ ...styles.ohlcValue, color: ohlc.close >= ohlc.open ? '#00ff94' : '#ff4060' }}>{ohlc.close.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
      {sessionCandles.length === 0 && (
        <div style={styles.noData}>
          No session data for {displayDate || new Date().toISOString().slice(0,10)}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    background: 'rgba(18,20,38,0.6)',
    backdropFilter: 'blur(16px)',
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(41,52,255,0.12)',
    boxShadow: '0 4px 32px rgba(0,0,0,0.4)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 18px',
    borderBottom: '1px solid rgba(41,52,255,0.1)',
  },
  title: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#f1f5f9',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
  },
  badge: {
    background: 'rgba(41,52,255,0.12)',
    border: '1px solid rgba(41,52,255,0.25)',
    color: '#8aa5ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: 8,
    boxShadow: '0 0 10px rgba(41,52,255,0.15)',
  },
  dateBadge: {
    marginLeft: 'auto',
    fontSize: 11,
    color: '#3a4055',
  },
  chartContainer: {
    position: 'relative',
    width: '100%',
    height: 500,
  },
  periodSeparator: {
    position: 'absolute',
    top: 8,
    bottom: 32,
    width: 2,
    background: 'rgba(180, 170, 220, 0.55)',
    pointerEvents: 'none',
    zIndex: 5,
  },
  ohlcOverlay: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 10,
    background: 'rgba(8, 11, 20, 0.82)',
    border: '1px solid rgba(147, 163, 184, 0.2)',
    borderRadius: 8,
    padding: '8px 12px',
    pointerEvents: 'none',
    minWidth: 160,
  },
  ohlcTime: {
    fontSize: 10,
    fontFamily: 'monospace',
    color: '#94a3b8',
    marginBottom: 4,
  },
  ohlcRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  ohlcLabel: {
    fontSize: 11,
    fontFamily: 'monospace',
    color: '#64748b',
    width: 14,
  },
  ohlcValue: {
    fontSize: 12,
    fontFamily: 'monospace',
    fontWeight: 700,
    color: '#ffffff',
  },
  legendDot: {
    fontSize: 11,
    color: '#94a3b8',
    marginLeft: 'auto',
  },
  legendDot2: {
    fontSize: 11,
    color: '#94a3b8',
  },
  loading: {
    padding: 40,
    textAlign: 'center',
    color: '#3a4055',
    fontSize: 13,
  },
  noData: {
    padding: '8px 16px',
    textAlign: 'center',
    color: '#4a5568',
    fontSize: 13,
  },
  markersRow: {
    display: 'flex',
    gap: 16,
    padding: '10px 16px',
    borderTop: '1px solid #334155',
    flexWrap: 'wrap',
  },
  marker: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  markerLabel: {
    fontSize: 11,
    color: '#64748b',
  },
  markerVal: {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#e2e8f0',
  },
};
